"use client";

import { useState } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Icon from "@/components/ui/AppIcon";
import { useLanguage } from "@/context/LanguageContext";

export default function BookingPage() {
  const { t } = useLanguage();

  const activities = [
    // Land Adventures
    t("act_buggy") + " (1h30 – 55€)",
    t("act_horse") + " – " + t("price_individual") + " – 20€",
    t("act_horse") + " – " + t("price_normal") + " – 15€",
    t("act_horse") + " – " + t("price_pro") + " – 25€",
    t("act_camel") + " (1h – 18€/person)",
    t("act_quad") + " (1h30 – 23€)",
    t("act_quad") + " (2h – 15€)",
    t("act_quad") + " (3h – 50€)",
    t("act_caleche") + " – " + t("price_croc") + " (10€/person)",
    t("act_caleche") + " – " + t("price_lagoon") + " (10€)",
    t("act_caleche") + " – " + t("price_market") + " (10€)",
    t("act_caleche_horse") + " (" + t("note_min2") + ")",
    // Sea Activities
    t("act_jetski") + " (30 min – 50€/jet)",
    t("act_jetski") + " (1h30 – 80€/person)",
    t("act_boat") + " (20€/person)",
    t("act_parasailing") + " (30€/person)",
    t("act_water") + " (25€/person)",
    t("act_pirate") + " (18.5€/person)",
    // Excursions
    t("act_island") + " (10€/person)",
    t("act_ksar") + " – " + t("price_standard") + " (60€/person)",
    t("act_ksar") + " – " + t("price_tent") + " (80€/person)",
    t("act_tataouine") + " (50€/person)",
  ];

  interface FormData {
    fullName: string;
    email: string;
    phone: string;
    numberOfPersons: string;
    selectedActivity: string;
    preferredDate: string;
    preferredTime: string;
    notes: string;
  }

  interface FormErrors {
    fullName?: string;
    email?: string;
    phone?: string;
    selectedActivity?: string;
    preferredDate?: string;
    preferredTime?: string;
  }

  const [formData, setFormData] = useState<FormData>({
    fullName: "",
    email: "",
    phone: "",
    numberOfPersons: "1",
    selectedActivity: "",
    preferredDate: "",
    preferredTime: "",
    notes: "",
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const validate = (): boolean => {
    const newErrors: FormErrors = {};
    if (!formData.fullName.trim()) newErrors.fullName = t("err_fullname");
    if (formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t("err_email_invalid");
    }
    if (!formData.phone.trim()) newErrors.phone = t("err_phone");
    if (!formData.selectedActivity) newErrors.selectedActivity = t("err_activity");
    if (!formData.preferredDate) newErrors.preferredDate = t("err_date");
    if (!formData.preferredTime) newErrors.preferredTime = t("err_time");
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    setSubmitError("");

    try {
      const whatsappNumber = "21654604948";
      const message = [
        `🌴 *New Booking Request*`,
        ``,
        `*Name:* ${formData.fullName}`,
        formData.email ? `*Email:* ${formData.email}` : null,
        `*Phone:* ${formData.phone}`,
        `*Persons:* ${formData.numberOfPersons}`,
        `*Activity:* ${formData.selectedActivity}`,
        `*Date:* ${formData.preferredDate}`,
        `*Time:* ${formData.preferredTime}`,
        formData.notes ? `*Notes:* ${formData.notes}` : null,
      ]
        .filter(Boolean)
        .join("\n");

      const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`;
      window.open(whatsappUrl, "_blank");
      setIsSuccess(true);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Something went wrong. Please try again.";
      setSubmitError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass = (field: keyof FormErrors) =>
    `w-full px-4 py-3 rounded-xl border bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 transition-all ${
      errors[field]
        ? "border-red-400 focus:ring-red-300" :"border-border focus:ring-primary/30 focus:border-primary"
    }`;

  return (
    <>
      <Header />
      <main className="pt-24 pb-20 bg-background min-h-screen">
        <div className="container mx-auto px-6">
          <div className="max-w-2xl mx-auto">

            {/* Page Header */}
            <div className="text-center mb-10">
              <span className="text-accent font-semibold text-sm uppercase tracking-wider">
                {t("booking_badge")}
              </span>
              <h1 className="font-serif text-4xl md:text-5xl font-bold text-foreground mt-3 mb-4">
                {t("booking_title")}
              </h1>
              <p className="text-muted-foreground text-lg">
                {t("booking_subtitle")}
              </p>
            </div>

            {/* Success State */}
            {isSuccess ? (
              <div className="bg-card rounded-3xl p-12 shadow-md text-center">
                <div className="w-20 h-20 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Icon name="CheckCircleIcon" size={48} className="text-success" />
                </div>
                <h2 className="font-serif text-3xl font-bold text-foreground mb-4">
                  {t("booking_success_title")}
                </h2>
                <p className="text-muted-foreground text-lg mb-8">
                  {t("booking_success_msg")}
                </p>
                <button
                  onClick={() => {
                    setIsSuccess(false);
                    setFormData({
                      fullName: "",
                      email: "",
                      phone: "",
                      numberOfPersons: "1",
                      selectedActivity: "",
                      preferredDate: "",
                      preferredTime: "",
                      notes: "",
                    });
                  }}
                  className="px-8 py-3 bg-primary text-white rounded-full font-semibold hover:bg-primary/90 transition-all hover:scale-105"
                >
                  {t("booking_another")}
                </button>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                noValidate
                className="bg-card rounded-3xl p-8 md:p-10 shadow-md space-y-6"
              >
                {/* Full Name */}
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-2">
                    {t("booking_fullname")} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleChange}
                    placeholder="e.g. Ahmed Ben Ali"
                    className={inputClass("fullName")}
                  />
                  {errors.fullName && (
                    <p className="mt-1.5 text-sm text-red-500 flex items-center gap-1">
                      <Icon name="ExclamationCircleIcon" size={14} />
                      {errors.fullName}
                    </p>
                  )}
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-2">
                    {t("booking_email")}{" "}
                    <span className="text-muted-foreground font-normal">{t("booking_notes_optional")}</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="e.g. ahmed@email.com"
                    className={inputClass("email")}
                  />
                  {errors.email && (
                    <p className="mt-1.5 text-sm text-red-500 flex items-center gap-1">
                      <Icon name="ExclamationCircleIcon" size={14} />
                      {errors.email}
                    </p>
                  )}
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-2">
                    {t("booking_phone")} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="e.g. +216 22 995 210"
                    className={inputClass("phone")}
                  />
                  {errors.phone && (
                    <p className="mt-1.5 text-sm text-red-500 flex items-center gap-1">
                      <Icon name="ExclamationCircleIcon" size={14} />
                      {errors.phone}
                    </p>
                  )}
                </div>

                {/* Number of Persons */}
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-2">
                    {t("booking_persons")}
                  </label>
                  <input
                    type="number"
                    name="numberOfPersons"
                    value={formData.numberOfPersons}
                    onChange={handleChange}
                    min="1"
                    max="50"
                    className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                  />
                </div>

                {/* Selected Activity */}
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-2">
                    {t("booking_activity")} <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="selectedActivity"
                    value={formData.selectedActivity}
                    onChange={handleChange}
                    className={`${inputClass("selectedActivity")} cursor-pointer`}
                  >
                    <option value="">{t("booking_activity_placeholder")}</option>
                    <optgroup label={t("booking_land")}>
                      {activities.slice(0, 12).map((a) => (
                        <option key={a} value={a}>{a}</option>
                      ))}
                    </optgroup>
                    <optgroup label={t("booking_sea")}>
                      {activities.slice(12, 18).map((a) => (
                        <option key={a} value={a}>{a}</option>
                      ))}
                    </optgroup>
                    <optgroup label={t("booking_excursions")}>
                      {activities.slice(18).map((a) => (
                        <option key={a} value={a}>{a}</option>
                      ))}
                    </optgroup>
                  </select>
                  {errors.selectedActivity && (
                    <p className="mt-1.5 text-sm text-red-500 flex items-center gap-1">
                      <Icon name="ExclamationCircleIcon" size={14} />
                      {errors.selectedActivity}
                    </p>
                  )}
                </div>

                {/* Date & Time */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-foreground mb-2">
                      {t("booking_date")} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      name="preferredDate"
                      value={formData.preferredDate}
                      onChange={handleChange}
                      className={inputClass("preferredDate")}
                    />
                    {errors.preferredDate && (
                      <p className="mt-1.5 text-sm text-red-500 flex items-center gap-1">
                        <Icon name="ExclamationCircleIcon" size={14} />
                        {errors.preferredDate}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-foreground mb-2">
                      {t("booking_time")} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="time"
                      name="preferredTime"
                      value={formData.preferredTime}
                      onChange={handleChange}
                      className={inputClass("preferredTime")}
                    />
                    {errors.preferredTime && (
                      <p className="mt-1.5 text-sm text-red-500 flex items-center gap-1">
                        <Icon name="ExclamationCircleIcon" size={14} />
                        {errors.preferredTime}
                      </p>
                    )}
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-2">
                    {t("booking_notes")}{" "}
                    <span className="text-muted-foreground font-normal">{t("booking_notes_optional")}</span>
                  </label>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    rows={4}
                    placeholder={t("booking_notes_placeholder")}
                    className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all resize-none"
                  />
                </div>

                {/* Submit Error */}
                {submitError && (
                  <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">
                    <Icon name="ExclamationCircleIcon" size={18} />
                    <span>{submitError}</span>
                  </div>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full flex items-center justify-center gap-3 px-8 py-4 bg-[#25D366] text-white rounded-full font-semibold text-lg hover:bg-[#1ebe5d] transition-all hover:scale-[1.02] disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100"
                >
                  {isSubmitting ? (
                    <>
                      <Icon name="ArrowPathIcon" size={22} className="animate-spin" />
                      <span>{t("booking_sending")}</span>
                    </>
                  ) : (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
                        <path d="M12 0C5.373 0 0 5.373 0 12c0 2.124.558 4.118 1.532 5.845L.057 23.428a.75.75 0 00.916.916l5.583-1.475A11.953 11.953 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.907 0-3.694-.504-5.24-1.385l-.374-.217-3.875 1.023 1.023-3.875-.217-.374A9.953 9.953 0 012 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z"/>
                      </svg>
                      <span>{t("booking_submit")}</span>
                    </>
                  )}
                </button>

                <p className="text-center text-sm text-muted-foreground">
                  {t("booking_footer_note")}
                </p>
              </form>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}